import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import { getPageDetails } from '../util/pagination'
import { COUNT_CAP } from '../util/searchCounts'
import clsx from 'clsx'

type Props = {
    offset: number
    limit: number
    total: number
    onChange: (offset: number, limit: number) => void
    loading?: boolean
}

/* Quiet ghost buttons — no borders or shadows — so the pager sits under the
   table as a footer strip rather than competing with the rows. The active page
   is deliberately neutral ink, not arwen red: next to a Severe column a red
   highlight would read as an alert. */
const PAGE_BUTTON_CLASSES =
    'inline-flex h-7 min-w-[28px] items-center justify-center rounded-md px-1.5 text-xs text-ink-600 hover:bg-ink-100 transition-colors duration-150 active:scale-[0.98]'
const ACTIVE_PAGE_BUTTON_CLASSES =
    'inline-flex h-7 min-w-[28px] items-center justify-center rounded-md px-1.5 text-xs font-semibold bg-ink-100 text-ink-900 transition-colors duration-150 active:scale-[0.98]'
const CHEVRON_BUTTON_CLASSES =
    'inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-600 hover:bg-ink-100 disabled:text-ink-300 disabled:hover:bg-transparent transition-colors duration-150 active:scale-95'
const ELLIPSIS_CLASSES =
    'hidden md:inline-flex h-7 items-center px-1 text-xs text-ink-400 select-none'

export default function Pagination(props: Props) {
    const { offset, limit, total, onChange, loading } = props

    const isCapped = total >= COUNT_CAP
    const pages = Math.ceil(total / limit)
    const hasPrevious = offset > 0
    const hasNext = offset + limit < total

    function handlePageClick(page: number) {
        onChange((page - 1) * limit, limit)
    }

    function handlePreviousClick() {
        onChange(offset - limit, limit)
    }

    function handleNextClick() {
        onChange(offset + limit, limit)
    }

    function handleSetLimit(newLimit: number) {
        onChange(offset, newLimit)
    }

    function generatePageButtons() {
        const buttons = []

        // We don't want to show too many page buttons, let's say 5 max
        const maxDisplayButtons = 7

        const pageDetails = getPageDetails(maxDisplayButtons, total, offset, limit)

        if (pageDetails.firstPageToDisplay > 1) {
            buttons.push(
                <button
                    key={'1'}
                    onClick={() => handlePageClick(1)}
                    className={clsx(PAGE_BUTTON_CLASSES, 'hidden md:inline-flex')}
                >
                    <span>1</span>
                </button>
            )
            if (pageDetails.firstPageToDisplay > 2) {
                buttons.push(
                    <span key={'first'} className={ELLIPSIS_CLASSES} aria-hidden="true">
                        &#8230;
                    </span>
                )
            }
        }

        for (let i = pageDetails.firstPageToDisplay; i <= pageDetails.lastPageToDisplay; i++) {
            const isCurrent = Math.round(offset / limit) + 1 === i

            buttons.push(
                isCurrent ? (
                    <button
                        key={i}
                        onClick={() => handlePageClick(i)}
                        aria-current="page"
                        className={ACTIVE_PAGE_BUTTON_CLASSES}
                    >
                        {i}
                    </button>
                ) : (
                    <button
                        key={i}
                        onClick={() => handlePageClick(i)}
                        className={clsx(PAGE_BUTTON_CLASSES, 'hidden md:inline-flex')}
                    >
                        {i}
                    </button>
                )
            )
        }

        if (pageDetails.lastPageToDisplay < pageDetails.totalPages) {
            if (pageDetails.lastPageToDisplay < pageDetails.totalPages - 1) {
                buttons.push(
                    <span key={'last'} className={ELLIPSIS_CLASSES} aria-hidden="true">
                        &#8230;
                    </span>
                )
            }
            buttons.push(
                <button
                    key={pages}
                    onClick={() => handlePageClick(pages)}
                    className={clsx(PAGE_BUTTON_CLASSES, 'hidden md:inline-flex')}
                >
                    {pages}
                </button>
            )
        }

        return buttons
    }

    return (
        <div className="flex min-h-[28px] items-center bg-white">
            {loading ? (
                <div className="flex w-full animate-pulse flex-row items-center">
                    <div className="h-7 w-full rounded-md bg-ink-100"></div>
                </div>
            ) : (
                <div className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-2">
                    <nav className="inline-flex items-center gap-1" aria-label="Pagination">
                        <select
                            id="limit"
                            name="limit"
                            className="mr-1 h-7 rounded-md border-ink-200 py-0 pl-2 pr-7 text-xs text-ink-600 focus:border-arwen-500 focus:outline-none focus:ring-arwen-500"
                            defaultValue={limit}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                const newLimit = event.target.value
                                handleSetLimit(Number(newLimit))
                            }}
                        >
                            <option>5</option>
                            <option>10</option>
                            <option>20</option>
                            <option>50</option>
                            <option>100</option>
                        </select>
                        {total > limit ? (
                            <>
                                <button
                                    onClick={handlePreviousClick}
                                    disabled={!hasPrevious}
                                    className={CHEVRON_BUTTON_CLASSES}
                                >
                                    <span className="sr-only">Previous</span>
                                    <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                                </button>

                                {generatePageButtons()}
                                <button
                                    onClick={handleNextClick}
                                    disabled={!hasNext}
                                    className={CHEVRON_BUTTON_CLASSES}
                                >
                                    <span className="sr-only">Next</span>
                                    <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                                </button>
                            </>
                        ) : null}
                    </nav>
                    <p className="text-xs tabular-nums text-ink-500">
                        {offset + 1}&#8211;
                        {offset + limit > total ? total : offset + limit} of{' '}
                        {isCapped ? '10,000+' : total.toLocaleString('en-GB')}
                    </p>
                </div>
            )}
        </div>
    )
}
